import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const inputVariants = cva(
  'flex text-text-primary transition-all duration-200 ease-out placeholder:text-text-tertiary/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-surface-tertiary border border-border/50 hover:border-border focus:border-primary',
        ghost:
          'bg-surface-secondary/80 border border-border/30 hover:border-border/60 focus:border-primary/70',
        elevated:
          'bg-surface-elevated border border-border/20 hover:border-border/40 focus:border-primary shadow-sm',
        minimal:
          'bg-transparent border-b border-border/50 rounded-none hover:border-border focus:border-primary px-0',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-base',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      rounded: 'md',
      fullWidth: true,
    },
  }
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, fullWidth, rounded, ...props }, ref) => {
    return (
      <input
        className={cn(
          inputVariants({ variant, size, fullWidth, rounded }),
          'placeholder:text-text-tertiary/60',
          'focus:placeholder:text-text-tertiary/40',
          'selection:bg-primary/20 selection:text-text-primary',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
